import { useQuery } from '@tanstack/react-query';
import { CreditCard, Check, ExternalLink, Receipt } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';

export function SettingsBillingPage() {
  const plans = useQuery({
    queryKey: ['billing', 'plans-auth'],
    queryFn: async () => (await api.get('/api/billing/plans')).data,
  });

  const subscription = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => (await api.get('/api/billing/subscription')).data,
  });

  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: async () => (await api.get('/api/billing/invoices')).data,
  });

  async function openPortal() {
    try {
      const res = await api.post('/api/billing/portal', { returnUrl: window.location.href });
      window.location.href = res.data.portalUrl;
    } catch (err) {
      toast.error('Billing portal unavailable', extractApiError(err).message);
    }
  }

  async function subscribe(planCode: 'starter' | 'professional') {
    try {
      const currency = plans.data?.currency ?? 'GBP';
      const period = plans.data?.billingPeriod ?? 'monthly';
      const res = await api.post('/api/billing/checkout', {
        planCode,
        currency,
        billingPeriod: period,
        successUrl: `${window.location.origin}/settings/billing?checkout=success`,
        cancelUrl: `${window.location.origin}/settings/billing?checkout=canceled`,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      toast.error('Checkout failed', extractApiError(err).message);
    }
  }

  const currentSub = subscription.data?.subscription;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Current plan, available plans, and invoices.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Current subscription</CardTitle>
              <CardDescription>Change plan, cancel, or download invoices via the Stripe portal.</CardDescription>
            </div>
            {currentSub && (
              <Button variant="outline" onClick={openPortal}>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Billing portal
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {currentSub ? (
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Plan</div>
                <div className="font-display text-xl">{currentSub.plan.name}</div>
                <Badge variant={currentSub.status === 'active' ? 'success' : 'warning'} className="mt-1">{currentSub.status}</Badge>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Billing</div>
                <div className="font-medium">{currentSub.currency} · {currentSub.billingPeriod}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Next renewal</div>
                <div className="font-medium">{new Date(currentSub.currentPeriodEnd).toLocaleDateString()}</div>
              </div>
              {currentSub.cancelAtPeriodEnd && (
                <div>
                  <Badge variant="warning">Cancels at period end</Badge>
                </div>
              )}
            </div>
          ) : (
            <EmptyState
              icon={CreditCard}
              title="No active subscription"
              description="Subscribe to a plan to unlock production usage + monthly page credits."
            />
          )}
        </CardContent>
      </Card>

      {/* Plan picker */}
      {!currentSub && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg">Plans</h2>
            <div className="text-xs text-muted-foreground">
              Showing {plans.data?.currency ?? '—'} · {plans.data?.billingPeriod ?? '—'} (detected: {plans.data?.detected?.region})
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {plans.data?.plans?.map((p: Plan) => (
              <Card key={p.code} className={p.code === 'professional' ? 'border-primary' : ''}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CardTitle>{p.name}</CardTitle>
                    {p.code === 'professional' && <Badge>Recommended</Badge>}
                  </div>
                  <CardDescription>{p.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="font-display text-3xl">{p.price?.display}</span>
                    <span className="text-sm text-muted-foreground">/{p.price?.billingPeriod ?? 'month'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {p.monthlyPageCredits.toLocaleString()} pages/mo · up to {p.maxSeats || '?'} seats
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {p.includedFeatures?.slice(0, 6).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={p.code === 'professional' ? 'default' : 'outline'}
                    onClick={() => subscribe(p.code as 'starter' | 'professional')}
                  >
                    Subscribe to {p.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Need more seats or a private deployment? <a className="text-primary hover:underline" href="mailto:sales@praxara.io">Contact sales</a> for Enterprise pricing.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>Latest invoices from Stripe. Full history available in billing portal.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading...</div>
          ) : invoices.data?.invoices?.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No invoices yet"
              description="Your first invoice will appear here once your subscription is active."
              className="m-4"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.data?.invoices?.map((inv: Invoice) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-xs">{inv.number ?? inv.stripeInvoiceId.slice(-8)}</TableCell>
                    <TableCell className="text-sm">{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-sm">
                      {inv.currency === 'GBP' ? '?' : inv.currency === 'EUR' ? '?' : '$'}
                      {(inv.total / 100).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'secondary'}>
                        {inv.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {inv.hostedInvoiceUrl && (
                        <Button asChild variant="ghost" size="sm">
                          <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface Plan {
  code: string;
  name: string;
  description: string;
  monthlyPageCredits: number;
  maxSeats: number;
  includedFeatures: string[];
  price: {
    display: string;
    billingPeriod: string;
  } | null;
}

interface Invoice {
  id: string;
  number: string | null;
  stripeInvoiceId: string;
  currency: string;
  total: number;
  status: string;
  hostedInvoiceUrl: string | null;
  createdAt: string;
}
